import { useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import {
	type FiltersType as Filters,
	type QuickFilters,
	useDeserialisedFilters,
	useFilters,
	useQuickFilters,
	useSimpleSearch,
} from '@atlassian/jira-business-filters';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	BOARD_FIELD_CATEGORY,
	BOARD_FIELD_COMPONENTS,
	BOARD_FIELD_LABEL,
	BOARD_FIELD_SELECT,
	BOARD_FIELD_USER,
	EMPTY_FILTER_VALUE,
} from '../../common/constants';
import type { BoardIssueOld, BoardIssueFieldOld } from '../../common/types';
import { useBoardStore } from '../board-store';
import {
	type ExpandedFilter,
	FILTER_MAP,
	filterByCategory,
	filterByLabels,
	filterByProjectComponents,
	filterBySelect,
	filterByUser,
	filterIssuesByText,
	getExpandedFilters,
	QUICK_FILTER_MAP,
	filterKeyToIssueFieldKey,
} from './utils';

export type BoardCombinedFilters = Filters | QuickFilters | null;
export type UseBoardFiltersReturn = {
	isFiltering: boolean;
	filter: (arg1: BoardIssueOld[]) => BoardIssueOld[];
	combinedFilters: BoardCombinedFilters;
	filteredFieldIds?: string[];
	isLoading: boolean;
};

const EMPTY_ISSUE_ARRAY: BoardIssueOld[] = [];

// All filter***2 methods are for filtering by transformed extended fields.
// All will be renamed to filter*** once board field transformation is rolled out, old methods will be removed
const filterByExtendedField = (extendedField: BoardIssueFieldOld, filter: ExpandedFilter) => {
	switch (extendedField.type) {
		case BOARD_FIELD_COMPONENTS:
			return filterByProjectComponents(filter, extendedField);
		case BOARD_FIELD_CATEGORY:
			return filterByCategory(filter, extendedField);
		case BOARD_FIELD_LABEL:
			return filterByLabels(filter, extendedField);
		case BOARD_FIELD_USER:
			return filterByUser(filter, extendedField);
		case BOARD_FIELD_SELECT:
			return filterBySelect(filter, extendedField);
		default:
			return false;
	}
};

const filterByExtendedFields = (
	filters: Filters,
	issue: BoardIssueOld,
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	categoryFieldId: string | void,
	expandedFilters: ExpandedFilter[],
) => {
	if (issue.issueDetails == null) {
		return true;
	}
	const issueFieldMap = keyBy(issue.issueDetails.fields, 'fieldId');
	return expandedFilters.every((filter) => {
		const issueField = issueFieldMap[filter.fieldKey];
		if (issueField == null) {
			return filter.hasEmptyFilter;
		}
		return filterByExtendedField(issueField, filter);
	});
};

const filterIssues = (
	filters: Filters,
	issues: BoardIssueOld[],
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	categoryFieldId: string | void,
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issueDetailFields: string[] | void,
): BoardIssueOld[] => {
	if (issues.length === 0) {
		return EMPTY_ISSUE_ARRAY;
	}

	if (filters === null || isEmpty(filters)) {
		return issues;
	}
	// reads filter = {assignee: [{ value: 'aman_account_id' }, { value: 'gorkem_account_id' }]} and converts to
	// [{issueFilterKey: 'assigneeKey', issueFilterValues: ['aman_account_id', 'gorkem_account_id']}]
	const filterValuesMap = Object.entries(filters).reduce(
		(acc, [filterKey, filterValues]) => {
			// @ts-expect-error - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type
			if (FILTER_MAP[filterKey] && Array.isArray(filterValues) && filterValues.length > 0) {
				acc.push({
					// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly assignee: { readonly issueKey: "assigneeKey"; readonly filterValueKey: "value"; }; readonly priority: { readonly issueKey: "priorityName"; readonly filterValueKey: "value"; }; readonly labels: { ...; }; readonly issuetype: { ...; }; }'.
					issueFilterKey: FILTER_MAP[filterKey].issueKey,
					issueFilterValues: new Set<string>(
						// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly assignee: { readonly issueKey: "assigneeKey"; readonly filterValueKey: "value"; }; readonly priority: { readonly issueKey: "priorityName"; readonly filterValueKey: "value"; }; readonly labels: { ...; }; readonly issuetype: { ...; }; }'.
						filterValues.map((val) => val[FILTER_MAP[filterKey].filterValueKey]),
					),
				});
			}
			return acc;
		},
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		[] as Array<{ issueFilterKey: keyof BoardIssueOld; issueFilterValues: Set<string | null> }>,
	);

	const expandedFilters = getExpandedFilters(filters, categoryFieldId, issueDetailFields);
	return issues
		.filter(
			(issue) =>
				isOptimisticIssue(issue.id) ||
				!filterValuesMap.some(({ issueFilterKey, issueFilterValues }) => {
					const issueField = Array.isArray(issueFilterKey)
						? issueFilterKey.reduce((fieldAccumulator, key) => fieldAccumulator?.[key], issue)
						: issue[issueFilterKey];
					if (Array.isArray(issueField)) {
						return !issueField.some((value) => issueFilterValues.has(value));
					}

					return issueFilterValues.has(EMPTY_FILTER_VALUE)
						? issueField && !issueFilterValues.has(issueField)
						: !issueFilterValues.has(issueField);
				}),
		)
		.filter(
			(issue) =>
				isOptimisticIssue(issue.id) ||
				filterByExtendedFields(filters, issue, categoryFieldId, expandedFilters),
		);
};

const quickFilterIssues = (
	quickFilters: QuickFilters,
	issues: BoardIssueOld[],
	atlassianAccountId: string | null,
): BoardIssueOld[] =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Object.entries(quickFilters).reduce((filteredIssues, [filterKey, filterValue]: [any, any]) => {
		if (filterValue === true) {
			return filterKey === 'ASSIGNED_TO_ME'
				? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly ASSIGNED_TO_ME: (issues: any, atlassianAccountId: string | null, newIssues: number[]) => any; readonly DONE: (issues: any) => any; readonly DUE_THIS_WEEK: (issues: any, newIssues: any) => any; }'.
					QUICK_FILTER_MAP[filterKey](filteredIssues, atlassianAccountId)
				: // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly ASSIGNED_TO_ME: (issues: any, atlassianAccountId: string | null, newIssues: number[]) => any; readonly DONE: (issues: any) => any; readonly DUE_THIS_WEEK: (issues: any, newIssues: any) => any; }'.
					QUICK_FILTER_MAP[filterKey](filteredIssues);
		}
		return filteredIssues;
	}, issues);

const EMPTY_FIELD_IDS: string[] = [];

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useBoardFilters = (): UseBoardFiltersReturn => {
	const [simpleSearch] = useSimpleSearch();
	const { filters, isLoading } = useDeserialisedFilters();

	const { jql: filtersJql } = useFilters();
	const { quickFiltersJql, quickFilters } = useQuickFilters();
	const { atlassianAccountId } = useTenantContext();
	const [{ categoryFieldId, issueDetailFields }] = useBoardStore();

	const filter = useCallback(
		(issues: BoardIssueOld[]) => {
			// we always want to filter issues with search text
			const issuesFilteredBySearch = filterIssuesByText(issues, simpleSearch);

			if (!isEmpty(filters))
				return filterIssues(filters, issuesFilteredBySearch, categoryFieldId, issueDetailFields);
			if (quickFiltersJql !== null && quickFilters)
				return quickFilterIssues(quickFilters, issuesFilteredBySearch, atlassianAccountId);

			return issuesFilteredBySearch;
		},
		[
			simpleSearch,
			filters,
			categoryFieldId,
			issueDetailFields,
			quickFiltersJql,
			quickFilters,
			atlassianAccountId,
		],
	);

	const isFiltering = useMemo(
		() =>
			!isEmpty(filters) ||
			(quickFiltersJql !== null && quickFiltersJql !== undefined) ||
			(simpleSearch !== undefined && simpleSearch !== ''),
		[filters, quickFiltersJql, simpleSearch],
	);

	const combinedFilters = useMemo(() => {
		if (!isEmpty(filters)) {
			return filters;
		}
		if (quickFiltersJql !== null && quickFiltersJql !== undefined) {
			return quickFilters;
		}
		return null;
	}, [filters, quickFilters, quickFiltersJql]);

	const filteredFieldIds = useMemo(
		() =>
			filters != null
				? Object.keys(filters).map((fieldKeyFromFilter) =>
						filterKeyToIssueFieldKey(fieldKeyFromFilter, categoryFieldId),
					)
				: EMPTY_FIELD_IDS,
		[categoryFieldId, filters],
	);

	const isDeserializingFilters = Boolean(!isFiltering && filtersJql);

	return useMemo(
		() => ({
			isFiltering,
			filter,
			combinedFilters,
			filteredFieldIds,
			isLoading: isLoading || isDeserializingFilters,
		}),
		[isFiltering, filter, combinedFilters, filteredFieldIds, isLoading, isDeserializingFilters],
	);
};
